import { idpService } from 'services/api/identity-provider.service';

export const HIDABLE_WIDGET_TYPES = ['outlook', 'calendar', 'todos', 'zammad', 'zammad-ticket', 'openproject', 'documenso', 'colibo'] as const;
export type HidableWidgetType = (typeof HIDABLE_WIDGET_TYPES)[number];

// Which campus provider keys each provider-backed widget supports.
// A type NOT listed here (e.g. 'matrix', 'text') is provider-independent and is gated by the caller
// instead — zammad, openproject and colibo on their own org flags, matrix on the REACT_APP_MATRIX_ENABLED feature flag.
export const WIDGET_PROVIDER_SUPPORT: Record<string, string[]> = {
    outlook: ['microsoft'], // MS mail only — no Gmail widget
    calendar: ['microsoft', 'google'],
    todos: ['microsoft', 'google']
};

export const isWidgetCampusEnabled = (type: string, enabledProviders: string[]): boolean => {
    const supported = WIDGET_PROVIDER_SUPPORT[type];
    if (!supported) return true; // provider-independent types are gated by the caller, not here
    return supported.some((key) => enabledProviders.includes(key));
};

// Zammad's campus-level gate lives outside dashboard_widget_providers — it's a distinct org flag,
// fetched separately. Shared here so both PersonalDashboard and the Widgets settings tab call it
// the same way instead of duplicating the idpService call.
export const fetchIsZammadCampusEnabled = (): Promise<boolean> =>
    idpService
        .getZammadStatus()
        .then((status) => status.is_zammad_enable)
        .catch(() => false);

// Same idea for OpenProject: its own org flag, read from the widget's status endpoint because branding
// does not carry it and showOrg() is manager-only.
export const fetchIsOpenProjectCampusEnabled = (): Promise<boolean> =>
    idpService
        .getOpenProjectStatus()
        .then((status) => status.is_openproject_enable)
        .catch(() => false);

// Documenso is gated the same way as OpenProject: its own org flag from the widget's status endpoint.
export const fetchIsDocumensoCampusEnabled = (): Promise<boolean> =>
    idpService
        .getDocumensoStatus()
        .then((status) => status.is_documenso_enable)
        .catch(() => false);

// The ticket widget is its own campus integration (not the Zammad one), readable by every member.
export const fetchIsZammadTicketCampusEnabled = (): Promise<boolean> =>
    idpService
        .getZammadTicketConfig()
        .then((config) => config.is_enabled)
        .catch(() => false);

// Same idea for Colibo: its own org flag, read from the widget's status endpoint.
export const fetchIsColiboCampusEnabled = (): Promise<boolean> =>
    idpService
        .getColiboStatus()
        .then((status) => status.is_colibo_enable)
        .catch(() => false);
